import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import { 
  Plus, Edit2, Trash2, FileText, X, Eye, 
  Search, RefreshCw, Calendar, Users, Shield, CheckCircle, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompanyPolicies = ({ user, onLogout }) => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [viewingPolicy, setViewingPolicy] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Search & Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  
  const [formData, setFormData] = useState({
    name: '',
    category: 'General',
    description: '',
    content: '',
    version: '1.0',
    effective_date: new Date().toISOString().split('T')[0],
    is_mandatory: true,
    requires_acknowledgment: false,
    document_url: ''
  });

  const categories = [
    'General',
    'Leave & Attendance',
    'Code of Conduct',
    'IT & Security',
    'Travel & Expense',
    'Health & Safety',
    'Harassment Prevention',
    'Data Privacy',
    'Remote Work',
    'Other'
  ];

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/hr-config/policies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPolicies(response.data.policies || []);
    } catch (error) {
      console.error('Error fetching policies:', error);
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter policies
  const filteredPolicies = policies.filter(policy => {
    const matchesSearch = !searchTerm || 
      policy.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      policy.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || policy.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // Statistics
  const stats = {
    total: policies.length,
    mandatory: policies.filter(p => p.is_mandatory).length,
    requiresAck: policies.filter(p => p.requires_acknowledgment).length,
    categories: [...new Set(policies.map(p => p.category))].length
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      if (editingPolicy) {
        await axios.put(`${API}/hr-config/policies/${editingPolicy.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Policy updated successfully!');
      } else {
        await axios.post(`${API}/hr-config/policies`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Policy created successfully!');
      }
      
      setShowForm(false);
      setEditingPolicy(null);
      resetForm();
      fetchPolicies();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (policy) => {
    setEditingPolicy(policy);
    setFormData({
      name: policy.name || '',
      category: policy.category || 'General',
      description: policy.description || '',
      content: policy.content || '',
      version: policy.version || '1.0',
      effective_date: policy.effective_date || new Date().toISOString().split('T')[0],
      is_mandatory: policy.is_mandatory !== false,
      requires_acknowledgment: policy.requires_acknowledgment || false,
      document_url: policy.document_url || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (policy) => {
    if (!window.confirm(`Delete policy "${policy.name}"?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/hr-config/policies/${policy.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Policy deleted successfully!');
      fetchPolicies();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: 'General',
      description: '',
      content: '',
      version: '1.0',
      effective_date: new Date().toISOString().split('T')[0],
      is_mandatory: true,
      requires_acknowledgment: false,
      document_url: ''
    });
    setEditingPolicy(null);
  };

  const getCategoryColor = (category) => {
    const colors = {
      'General': 'bg-blue-100 text-blue-700 border-l-blue-500',
      'Leave & Attendance': 'bg-green-100 text-green-700 border-l-green-500',
      'Code of Conduct': 'bg-purple-100 text-purple-700 border-l-purple-500',
      'IT & Security': 'bg-red-100 text-red-700 border-l-red-500',
      'Travel & Expense': 'bg-yellow-100 text-yellow-700 border-l-yellow-500',
      'Health & Safety': 'bg-pink-100 text-pink-700 border-l-pink-500',
      'Harassment Prevention': 'bg-orange-100 text-orange-700 border-l-orange-500',
      'Data Privacy': 'bg-indigo-100 text-indigo-700 border-l-indigo-500',
      'Remote Work': 'bg-teal-100 text-teal-700 border-l-teal-500',
      'Other': 'bg-gray-100 text-gray-700 border-l-gray-400'
    };
    return colors[category] || colors['Other'];
  };

  const getCategoryBadgeColor = (category) => {
    const colors = {
      'General': 'bg-blue-500 text-white',
      'Leave & Attendance': 'bg-green-500 text-white',
      'Code of Conduct': 'bg-purple-500 text-white',
      'IT & Security': 'bg-red-500 text-white',
      'Travel & Expense': 'bg-yellow-500 text-white',
      'Health & Safety': 'bg-pink-500 text-white',
      'Harassment Prevention': 'bg-orange-500 text-white',
      'Data Privacy': 'bg-indigo-500 text-white',
      'Remote Work': 'bg-teal-500 text-white',
      'Other': 'bg-gray-500 text-white'
    };
    return colors[category] || colors['Other'];
  };

  const canEdit = user?.role?.toLowerCase() === 'admin' || 
                  user?.role?.toLowerCase() === 'hr' || 
                  user?.role?.toLowerCase() === 'hr manager';

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>📜 Company Policies</h1>
            <p className="text-gray-600 mt-2">Manage and publish company policies and guidelines</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchPolicies}
              disabled={loading}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {canEdit && (
              <button
                onClick={() => { resetForm(); setShowForm(true); }}
                className="px-6 py-3 rounded-lg text-white font-medium flex items-center gap-2"
                style={{backgroundColor: '#2b3380'}}
              >
                <Plus className="w-5 h-5" />
                Add Policy
              </button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full" style={{backgroundColor: '#e8eaf6'}}>
              <FileText className="w-6 h-6" style={{color: '#2b3380'}} />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{color: '#2b3380'}}>{stats.total}</p>
              <p className="text-sm text-gray-500">Total Policies</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-red-100">
              <Shield className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{stats.mandatory}</p>
              <p className="text-sm text-gray-500">Mandatory</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-orange-100">
              <CheckCircle className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-orange-600">{stats.requiresAck}</p>
              <p className="text-sm text-gray-500">Needs Acknowledgment</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-purple-100">
              <BookOpen className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-600">{stats.categories}</p>
              <p className="text-sm text-gray-500">Categories</p>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search policies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Policies List */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
            <p className="mt-3 text-gray-500">Loading policies...</p>
          </div>
        ) : filteredPolicies.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <FileText className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-xl font-medium text-gray-500 mb-2">No policies found</p>
            <p className="text-gray-400">
              {searchTerm || filterCategory !== 'all' 
                ? 'Try different search terms or filters' 
                : canEdit ? 'Click "Add Policy" to create one' : 'Check back later for updates'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredPolicies.map((policy) => (
              <div 
                key={policy.id} 
                className={`bg-white rounded-lg shadow-md p-6 border-l-4 hover:shadow-lg transition-shadow ${getCategoryColor(policy.category)}`}
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-bold" style={{color: '#2b3380'}}>{policy.name}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryBadgeColor(policy.category)}`}>
                        {policy.category}
                      </span>
                      {policy.is_mandatory && (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          Mandatory
                        </span>
                      )}
                      {policy.requires_acknowledgment && (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                          Needs Ack
                        </span>
                      )}
                    </div>
                  </div>
                  {canEdit && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => setViewingPolicy(policy)}
                        className="p-2 hover:bg-green-50 rounded text-green-600"
                        title="Preview"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(policy)}
                        className="p-2 hover:bg-blue-50 rounded text-blue-600"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(policy)}
                        className="p-2 hover:bg-red-50 rounded text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                
                <p className="text-gray-700 mb-4 leading-relaxed">{policy.description}</p>
                
                <div className="flex flex-wrap justify-between items-center text-sm text-gray-500 pt-3 border-t">
                  <div className="flex items-center gap-4">
                    <span className="px-2 py-1 bg-gray-100 rounded text-xs">v{policy.version}</span>
                    <span className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      All Employees
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Effective: {policy.effective_date ? new Date(policy.effective_date).toLocaleDateString('en-IN') : 'N/A'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* View Policy Modal */}
        {viewingPolicy && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>📄 {viewingPolicy.name}</h2>
                  <button onClick={() => setViewingPolicy(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="flex flex-wrap gap-3 mb-4">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getCategoryBadgeColor(viewingPolicy.category)}`}>
                    {viewingPolicy.category}
                  </span>
                  <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-sm">
                    Version {viewingPolicy.version}
                  </span>
                  {viewingPolicy.is_mandatory && (
                    <span className="px-3 py-1 rounded-full bg-red-100 text-red-700 text-sm font-medium">
                      Mandatory
                    </span>
                  )}
                </div>

                {viewingPolicy.description && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <p className="text-gray-700">{viewingPolicy.description}</p>
                  </div>
                )}

                {viewingPolicy.content && (
                  <div className="mb-4">
                    <h4 className="font-medium text-gray-800 mb-2">Policy Content</h4>
                    <div className="bg-gray-50 rounded-lg p-4 whitespace-pre-wrap text-gray-700">
                      {viewingPolicy.content}
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    onClick={() => { handleEdit(viewingPolicy); setViewingPolicy(null); }}
                    className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    Edit Policy
                  </button>
                  <button
                    onClick={() => setViewingPolicy(null)}
                    className="px-4 py-2 text-white rounded-lg"
                    style={{backgroundColor: '#2b3380'}}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add/Edit Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>
                    {editingPolicy ? '✏️ Edit Policy' : '📜 Add New Policy'}
                  </h2>
                  <button onClick={() => { setShowForm(false); resetForm(); }} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Policy Name *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                        placeholder="e.g., Remote Work Policy"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Category</label>
                        <select
                          value={formData.category}
                          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          {categories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Version</label>
                        <input
                          type="text"
                          value={formData.version}
                          onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="1.0"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Effective Date</label>
                        <input
                          type="date"
                          value={formData.effective_date}
                          onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Document URL</label>
                        <input
                          type="url"
                          value={formData.document_url}
                          onChange={(e) => setFormData({ ...formData, document_url: e.target.value })}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="https://..."
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Description</label>
                      <input
                        type="text"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="Brief description of the policy"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Policy Content</label>
                      <textarea
                        value={formData.content}
                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        rows={5}
                        placeholder="Full policy content..."
                      />
                    </div>

                    <div className="flex items-center gap-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.is_mandatory}
                          onChange={(e) => setFormData({ ...formData, is_mandatory: e.target.checked })}
                          className="rounded text-blue-600"
                        />
                        <span className="text-sm">Mandatory Policy</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.requires_acknowledgment}
                          onChange={(e) => setFormData({ ...formData, requires_acknowledgment: e.target.checked })}
                          className="rounded text-blue-600"
                        />
                        <span className="text-sm">Requires Acknowledgment</span>
                      </label>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-700">
                        <strong>Note:</strong> This policy will be visible to all employees under Resources → Company Policies.
                      </p>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="px-6 py-3 text-white rounded-lg font-medium flex items-center gap-2"
                        style={{backgroundColor: submitting ? '#9e9e9e' : '#2b3380'}}
                      >
                        <FileText className="w-5 h-5" />
                        {submitting ? 'Saving...' : (editingPolicy ? 'Update Policy' : 'Create Policy')}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowForm(false); resetForm(); }}
                        className="px-6 py-3 border rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CompanyPolicies;
